import React, { useRef, useMemo, useEffect, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useLoader } from '@react-three/fiber'
import useGame from './stores/useGame.js'

export default function Dust() {
  const pointsRef = useRef()
  const circleTexture = useLoader(THREE.TextureLoader, '/Images/pallo.png')
  const space = useGame((state) => state.space)
  const particleCount = 3000

  // ✅ Camera positions used as dust cloud centers
  const spaceCenters = {
    wall: new THREE.Vector3(0, 2, 2),
    table: new THREE.Vector3(2, 2, 4),
    alley: new THREE.Vector3(98, 0, 4),
    arthouse: new THREE.Vector3(115, 5, 12),
    stage: new THREE.Vector3(125, 0, 15),
  }

  const [center, setCenter] = useState(spaceCenters[space]?.clone() || new THREE.Vector3())

  useEffect(() => {
    if (spaceCenters[space]) {
      setCenter(spaceCenters[space].clone())
    }
  }, [space])

  useFrame(() => {
    const target = spaceCenters[space]
    if (target && pointsRef.current) {
      // Smoothly interpolate to new dust location
      center.lerp(target, 0.05)
      pointsRef.current.position.copy(center)
    }

    // Float particle movement
    const posArray = pointsRef.current.geometry.attributes.position.array
    const time = Date.now() * 0.001
    for (let i = 0; i < particleCount; i++) {
      const yIndex = i * 3 + 1
      const xIndex = i * 3 + 0
      posArray[yIndex] += Math.sin(time + i) * 0.0001
      posArray[xIndex] += Math.sin(time * 0.5 + i) * 0.00005
    }
    pointsRef.current.geometry.attributes.position.needsUpdate = true
  })

  // Spread area of particles (relative to center)
  const positions = useMemo(() => {
    const pos = new Float32Array(particleCount * 3)
    for (let i = 0; i < particleCount; i++) {
      pos[i * 3 + 0] = (Math.random() - 0.5) * 10 // x range
      pos[i * 3 + 1] = Math.random() * 6 - 3    // y range
      pos[i * 3 + 2] = (Math.random() - 0.5) * 10 // z range
    }
    return pos
  }, [])

  const geometry = useMemo(() => {
    const geom = new THREE.BufferGeometry()
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    return geom
  }, [positions])


  // ✅ Define material settings per space
  const materialSettings = {
    home:     { color: 0xffffff, opacity: 0.1, size: 0.02 },
    wall:     { color: 0xffffff, opacity: 0.3, size: 0.025 },
    table:    { color: 0xffffff, opacity: 0.3, size: 0.025 },
    alley:    { color: 0xccccff, opacity: 0.08, size: 0.03 },
    arthouse: { color: 0xffccff, opacity: 0.1, size: 0.02 },
    stage:    { color: 'red', opacity: 0.6, size: 0.01 },
  }

  const current = materialSettings[space] || materialSettings.wall

  const material = useMemo(() => {
    return new THREE.PointsMaterial({
      size: current.size,
      color: current.color,
      map: circleTexture,
      transparent: true,
      alphaTest: 0.01,
      depthWrite: false,
      opacity: current.opacity,
      blending: THREE.AdditiveBlending,
    })
  }, [circleTexture, current])

  return (
    <points ref={pointsRef} geometry={geometry} material={material} />
  )
}
